/**
 * ContainerThresholdsView Component
 *
 * This component is responsible for displaying the sensor thresholds view for a specific container.
 * It groups sensors by their probe ID and displays each group with their respective sensor data.
 * Additionally, it visually indicates when sensor values are outside their defined thresholds.
 *
 * Props:
 * - containerId: Identifier for the container whose sensor data is being displayed.
 * - sensorsTableHeader: Object defining the headers for the sensor data table.
 * - sensorsData: Array of sensor data objects to be displayed.
 * - handleThresholdSaveClick: Function to handle saving changes to sensor thresholds.
 * - handleThresholdAdditiveSaveClick: Function to handle saving additive changes to sensor thresholds.
 *
 * The component utilizes the `ThresholdAccordion` for displaying detailed threshold controls for each sensor.
 * It also applies specific styling to sensor values that are outside their thresholds, using arrows and color coding
 * to indicate whether the value is above or below the expected range.
 */

import React, { useState } from "react";
import { Table, Button, Modal, Form, Tab, Tabs } from "react-bootstrap";
import { EditOutlined,   DeleteOutlined } from "../../../../helpers/icons/antDesignIcons";
import ThresholdAccordion from "./ThresholdAccordion";
import "../ContainerMoreInfoPage.scss";
import moment from "moment-timezone";
import useTranslation from "../../../../hooks/useTranslation";
import { useHandleDeviceLinkClick } from "../../../../utils/navigationUtils";

// ─── helpers ────────────────────────────────────────────────────────────────

const getThresholdArrowAndClass = (sensor) => {
  if (sensor.min_value === null || sensor.max_value === null)
    return { arrow: "", className: "" };
  if (sensor.sens_value < sensor.min_value)
    return { arrow: " ↓", className: "red-text-bold" };
  if (sensor.sens_value > sensor.max_value)
    return { arrow: " ↑", className: "red-text-bold" };
  return { arrow: "", className: "" };
};

// ─── Threshold Edit Modal ────────────────────────────────────────────────────

const ThresholdEditModal = ({
  show,
  onHide,
  title,
  currentMin,
  currentMax,
  onSaveAbsolute,
  onSaveAdditive,
  onDelete,
  hasThresholds,
  isSaving,
  t,
}) => {
  const [activeTab, setActiveTab] = useState("absolute");
  const [absMin, setAbsMin] = useState(currentMin ?? "");
  const [absMax, setAbsMax] = useState(currentMax ?? "");
  const [minShift, setMinShift] = useState(0);
  const [maxShift, setMaxShift] = useState(0);

  // Reset whenever the modal opens for a different sensor/probe
  React.useEffect(() => {
    if (show) {
      setActiveTab("absolute");
      setAbsMin(currentMin ?? "");
      setAbsMax(currentMax ?? "");
      setMinShift(0);
      setMaxShift(0);
    }
  }, [show, currentMin, currentMax]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="sm"
      className="threshold-edit-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="threshold-edit-modal__title">
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Current values info strip */}
        {(currentMin !== null || currentMax !== null) && (
          <div className="threshold-edit-modal__current-values">
            <span>{t("current") || "Current"}:&nbsp;</span>
            <strong>
              {currentMin ?? "N/A"} – {currentMax ?? "N/A"}
            </strong>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="threshold-edit-modal__tabs mt-2"
        >
          {/* ── Absolute tab ── */}
          <Tab
            eventKey="absolute"
            title={t("set_absolute") || "Set (=)"}
            className="threshold-edit-modal__tab-pane"
          >
            <Form className="threshold-edit-modal__form mt-3">
              <Form.Group>
                <Form.Label>{t("minValue")}</Form.Label>
                <Form.Control
                  type="number"
                  value={absMin}
                  onChange={(e) => setAbsMin(e.target.value)}
                  placeholder={currentMin !== null ? String(currentMin) : "—"}
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>{t("maxValue")}</Form.Label>
                <Form.Control
                  type="number"
                  value={absMax}
                  onChange={(e) => setAbsMax(e.target.value)}
                  placeholder={currentMax !== null ? String(currentMax) : "—"}
                />
              </Form.Group>
            </Form>
          </Tab>

          {/* ── Additive tab ── */}
          <Tab
            eventKey="additive"
            title={t("set_additive") || "Shift (±)"}
            className="threshold-edit-modal__tab-pane"
          >
            <p className="threshold-edit-modal__additive-hint mt-3">
              {t("additive_hint") ||
                "Shifts the threshold relative to the current measured value."}
            </p>
            <Form className="threshold-edit-modal__form">
              <Form.Group>
                <Form.Label>
                  {t("modify_min_threshold_with") || "Min shift"}
                </Form.Label>
                <Form.Control
                  type="number"
                  value={minShift}
                  onChange={(e) => setMinShift(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>
                  {t("modify_max_threshold_with") || "Max shift"}
                </Form.Label>
                <Form.Control
                  type="number"
                  value={maxShift}
                  onChange={(e) => setMaxShift(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="threshold-edit-modal__footer">
        {hasThresholds && (
          <Button
            variant="danger"
            disabled={isSaving}
            onClick={onDelete}
            className="me-auto"
          >
            <DeleteOutlined />
          </Button>
        )}
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t("cancel") || "Cancel"}
        </Button>
        <Button
          variant="primary"
          disabled={isSaving}
          onClick={() => {
            if (activeTab === "absolute") {
              onSaveAbsolute(parseFloat(absMin), parseFloat(absMax));
            } else {
              onSaveAdditive(parseFloat(minShift), parseFloat(maxShift));
            }
          }}
        >
          {isSaving ? "…" : t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const ContainerThresholdsView = ({
  containerId,
  sensorsTableHeader,
  sensorsData,
  handleThresholdSaveClick,
  handleThresholdAdditiveSaveClick,
  handleSingleSensorThresholdSave, // (sensor, min, max, mode) => Promise
  handleProbeLevelSave, // (probeid, sensors, min, max, mode) => Promise
  sensorMinThreshold,
  sensorMaxThreshold,
}) => {
  const { t } = useTranslation("locationsPage");
  const handleDeviceLinkClick = useHandleDeviceLinkClick();

  const [modalConfig, setModalConfig] = useState(null); // null = closed
  const [isSaving, setIsSaving] = useState(false);

  // ── Open helpers ──
  const openSensorModal = (sensor) => {
    setModalConfig({
      type: "sensor",
      sensor,
      title: `${t("probe") || "Probe"} ${sensor.probeid} — ${
        t("depth") || "Depth"
      } ${sensor.sens_num}`,
      currentMin: sensor.min_value,
      currentMax: sensor.max_value,
    });
  };

  const openProbeModal = (probeid, sensors) => {
    // Only consider temperature sensors for the modal reference values
    const tempSensors = sensors.filter((s) => s.sens_type === 2);
    const first = tempSensors[0];
    setModalConfig({
      type: "probe",
      probeid,
      sensors: tempSensors, // ← only temp sensors passed through
      title: `${t("probe") || "Probe"} ${probeid} — ${
        t("all_sensors") || "All sensors"
      }`,
      currentMin: first?.min_value ?? null,
      currentMax: first?.max_value ?? null,
    });
  };

  const closeModal = () => {
    if (!isSaving) setModalConfig(null);
  };

  // ── Save handlers passed to modal ──
  const handleSaveAbsolute = async (min, max) => {
    setIsSaving(true);
    try {
      if (modalConfig.type === "sensor") {
        await handleSingleSensorThresholdSave(
          modalConfig.sensor,
          min,
          max,
          "absolute",
        );
      } else {
        await handleProbeLevelSave(
          modalConfig.probeid,
          modalConfig.sensors,
          min,
          max,
          "absolute",
        );
      }
      setModalConfig(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAdditive = async (minShift, maxShift) => {
    setIsSaving(true);
    try {
      if (modalConfig.type === "sensor") {
        await handleSingleSensorThresholdSave(
          modalConfig.sensor,
          minShift,
          maxShift,
          "additive",
        );
      } else {
        await handleProbeLevelSave(
          modalConfig.probeid,
          modalConfig.sensors,
          minShift,
          maxShift,
          "additive",
        );
      }
      setModalConfig(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      if (modalConfig.type === "sensor") {
        await handleSingleSensorThresholdSave(
          modalConfig.sensor,
          null,
          null,
          null,
          true, // isDelete
        );
      } else {
        await handleProbeLevelSave(
          modalConfig.probeid,
          modalConfig.sensors,
          null,
          null,
          null,
          true, // isDelete
        );
      }
      setModalConfig(null);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Group sensors ──
  const groupedSensorsData = sensorsData
    .sort((a, b) => a.probeid - b.probeid)
    .reduce((acc, sensor) => {
      (acc[sensor.probeid] = acc[sensor.probeid] || []).push(sensor);
      return acc;
    }, {});

  return (
    <div>
      <ThresholdAccordion
        sensorsData={sensorsData}
        handleThresholdSaveClick={handleThresholdSaveClick}
        handleThresholdAdditiveSaveClick={handleThresholdAdditiveSaveClick}
        sensorMinThreshold={sensorMinThreshold}
        sensorMaxThreshold={sensorMaxThreshold}
      />
      <div className="container-thresholds-view-table__wrapper">
        <Table hover className="container-threshold-view-sensors-table">
          <thead>
            <tr className="table-header-tr">
              {Object.keys(sensorsTableHeader).map((key, index) => (
                <th
                  key={index}
                  className={`table-header__item ${key}-header`}
                  id={`ctmwt-${key}-header`}
                >
                  {sensorsTableHeader[key]}
                </th>
              ))}
              {/* Extra column for the per-sensor edit icon */}
              <th className="table-header__item ctmwt-edit-col-header" />
            </tr>
          </thead>

          <tbody>
            {Object.entries(groupedSensorsData).map(([probeid, sensors]) => (
              <React.Fragment key={probeid}>
                {/* ── Probe header row ── */}
                <tr>
                  <td colSpan="5" className="ctmwt-grouping-line">
                    {t("probe")}:{"  "}
                    <span
                      className="clickable-probeid"
                      onClick={(e) =>
                        handleDeviceLinkClick({ probeid, sensors }, e)
                      }
                    >
                      {" "}
                      {probeid}
                    </span>
                    <Button
                      size="sm"
                      className="ctmwt-probe-edit-btn"
                      title={t("edit_all")}
                      onClick={() => openProbeModal(probeid, sensors)}
                    >
                      {t("edit_all")}
                    </Button>
                  </td>
                </tr>

                {/* ── Individual sensor rows ── */}
                {sensors.map((sensor, index) => (
                  <tr key={index}>
                    {Object.keys(sensorsTableHeader).map((key, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={`table-data ${key}-data`}
                        id={`ctmwt-${key}-data`}
                      >
                        {key === "sens_value" ? (
                          <span
                            className={
                              getThresholdArrowAndClass(sensor).className
                            }
                          >
                            {`${sensor[key]}${
                              getThresholdArrowAndClass(sensor).arrow
                            }`}
                          </span>
                        ) : key === "min_value" && sensor[key] === null ? (
                          <p className="ctmwt-no-data">N/A</p>
                        ) : key === "max_value" && sensor[key] === null ? (
                          <p className="ctmwt-no-data">N/A</p>
                        ) : key === "min_value" && sensor[key] !== null ? (
                          sensor[key]
                        ) : key === "max_value" && sensor[key] !== null ? (
                          sensor[key]
                        ) : key === "sens_timestamp" ? (
                          <span style={{ color: "black" }}>
                            {moment(sensor[key])
                              .tz(
                                Intl.DateTimeFormat().resolvedOptions()
                                  .timeZone,
                              )
                              .format("YYYY.MM.DD HH:mm")}
                          </span>
                        ) : (
                          sensor[key]
                        )}
                      </td>
                    ))}

                    {/* Edit icon cell */}
                    {sensor.sens_type === 2 ? (
                      <td className="ctmwt-sensor-edit-cell">
                        <button
                          className="ctmwt-edit-icon-btn ctmwt-edit-icon-btn-row"
                          title={t("edit")}
                          onClick={() => openSensorModal(sensor)}
                          aria-label={`Szonda módosítása ${sensor.sens_num}`}
                        >
                          <EditOutlined />
                        </button>
                      </td>
                    ) : (
                      <td className="ctmwt-sensor-edit-cell"></td>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      {/* ── Edit Modal (single instance, reconfigured per target) ── */}
      {modalConfig && (
        <ThresholdEditModal
          show={!!modalConfig}
          onHide={closeModal}
          title={modalConfig.title}
          currentMin={modalConfig.currentMin}
          currentMax={modalConfig.currentMax}
          onSaveAbsolute={handleSaveAbsolute}
          onSaveAdditive={handleSaveAdditive}
          onDelete={handleDelete}
          hasThresholds={
            modalConfig.currentMin !== null && modalConfig.currentMax !== null
          }
          isSaving={isSaving}
          t={t}
        />
      )}
    </div>
  );
};

export default ContainerThresholdsView;
